import React from 'react'
import {
    Box,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Paper,
} from '@material-ui/core'

import { AuditCell } from './AuditCell'
import {
    AttributeSummary,
    AuditAttribute,
    AuditAttributeValue,
    AuditResidentTerm,
} from '../../types'

interface AuditTableProps {
    attributes: AuditAttribute[]
    attributeSummaryById: Record<number, AttributeSummary>
    residentTerms: AuditResidentTerm[] | undefined
    cellClasses: {
        dataCell: string
        matchedCell: string
        notMatchedCell: string
        missingRentRollCell: string
        missingDocumentCell: string
        cellContent: string
        cellValue: string
        cellIndicators: string
    }
    tableContainerClassName: string
    headerCellClassName: string
    headerCellClickableClassName: string
    headerCellContentClassName: string
    mismatchBadgeClassName: string
    onHeaderClick: (attribute: AuditAttribute) => void
    onCellClick: (
        attributeValue: AuditAttributeValue,
        attributeName: string,
        leaseDocumentFile: string,
        termId: number,
    ) => void
}

export const AuditTable: React.FC<AuditTableProps> = ({
    attributes,
    attributeSummaryById,
    residentTerms,
    cellClasses,
    tableContainerClassName,
    headerCellClassName,
    headerCellClickableClassName,
    headerCellContentClassName,
    mismatchBadgeClassName,
    onHeaderClick,
    onCellClick,
}) => {
    return (
        <TableContainer component={Paper} className={tableContainerClassName}>
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        {attributes.map((attr) => {
                            const mismatchCount =
                                attributeSummaryById[attr.id]?.mismatch_count
                            const formattedMismatchCount =
                                typeof mismatchCount === 'number' &&
                                mismatchCount > 0
                                    ? mismatchCount.toLocaleString()
                                    : null

                            return (
                                <TableCell
                                    key={attr.id}
                                    className={`${headerCellClassName} ${headerCellClickableClassName}`}
                                    onClick={() => onHeaderClick(attr)}
                                    onKeyDown={(event) => {
                                        if (
                                            event.key === 'Enter' ||
                                            event.key === ' '
                                        ) {
                                            event.preventDefault()
                                            onHeaderClick(attr)
                                        }
                                    }}
                                    role="button"
                                    tabIndex={0}
                                >
                                    <Box
                                        component="span"
                                        className={headerCellContentClassName}
                                    >
                                        <span>{attr.name}</span>
                                        {formattedMismatchCount !== null && (
                                            <span
                                                className={
                                                    mismatchBadgeClassName
                                                }
                                                title={`Mismatches: ${formattedMismatchCount}`}
                                                aria-label={`Mismatches: ${formattedMismatchCount}`}
                                            >
                                                {formattedMismatchCount}
                                            </span>
                                        )}
                                    </Box>
                                </TableCell>
                            )
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {residentTerms?.map((term) => (
                        <TableRow key={term.id} hover>
                            {attributes.map((attr) => {
                                const attrValue = term.attribute_values.find(
                                    (av) => av.attribute_id === attr.id,
                                )

                                return (
                                    <AuditCell
                                        key={`${term.id}-${attr.id}`}
                                        attribute={attr}
                                        attributeValue={attrValue}
                                        termId={term.id}
                                        selectedDocumentFile={
                                            term.selected_document_file
                                        }
                                        cellClasses={cellClasses}
                                        onCellClick={onCellClick}
                                    />
                                )
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    )
}
